import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { HttpHelperService } from '@helpers/http-helper.service';
import { UserHelperService } from '@helpers/user-helper.service';
import { AuthenticationService } from '@modules/authentication/authentication.service';
import { CustomersService } from '@modules/customers/customers.service';
import { HttpErrorsService } from '@services/http-errors/http-errors.service';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  from,
  mergeMap,
  Observable,
  retryWhen,
  switchMap,
  take,
  throwError,
  timer,
} from 'rxjs';
import { NgxSpinnerService } from '@node_modules/ngx-spinner';
import { AdminOtherUsersService } from '@helpers/admin-other-users.service';
import { MatDialog } from '@angular/material/dialog';
import { LocalStorageService } from '@services/common/local-storage.service';
import { SKIP_RETRY_BAD_GATEWAY } from './http-context-tokens';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    @Inject(UserHelperService)
    private userHelperService: UserHelperService,
    @Inject(HttpHelperService)
    private httpHelperService: HttpHelperService,
    @Inject(AuthenticationService) private authService: AuthenticationService,
    @Inject(HttpErrorsService) private httpErrorsService: HttpErrorsService,
    private readonly spinner: NgxSpinnerService,
    private injector: Injector,
    private dialog: MatDialog,
    private localStorageService: LocalStorageService
  ) {}

  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private isRefreshing = false;
  // upper bound for the impersonated-overlay refetch; on timeout the retry is
  // released anyway and the overlay applies late (previous fire-and-forget behaviour).
  private impersonatedOverlayTimeoutMs = 8000;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.userHelperService.getIdToken();
    const checkViaCep =
      this.httpHelperService.checkIfRouteNotIsPublic(request.url) && !this.httpHelperService.isPabxRoute(request.url);
    if (token && !checkViaCep) {
      request = request.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }
    return next.handle(request).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          !this.httpHelperService.checkIfRouteNotIsPublic(request.url) &&
          !this.httpHelperService.isPabxRoute(request.url)
        ) {
          return this.handle401Error(request, next);
        } else if (
          error instanceof HttpErrorResponse &&
          error.status === HttpStatusCode.BadGateway &&
          !request.context.get(SKIP_RETRY_BAD_GATEWAY)
        ) {
          return this.handleBadGatewayError(request, next);
        }

        void this.spinner.hide();
        return throwError(() => error);
      })
    );
  }

  private maxTentatives = 6;
  private delayTentativesMs = 1500;
  private handleBadGatewayError(request: HttpRequest<any>, next: HttpHandler) {
    this.spinner.show();
    return next.handle(request.clone()).pipe(
      retryWhen((errors) => {
        return errors.pipe(
          mergeMap((error: HttpErrorResponse, retryCount: number) => {
            if (error.status === HttpStatusCode.BadGateway && retryCount < this.maxTentatives) {
              this.spinner.show();
              return timer(this.delayTentativesMs);
            }

            return throwError(() => error);
          }),
          take(this.maxTentatives)
        );
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      }),
      finalize(() => {
        this.spinner.hide();
      })
    );
  }
  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      try {
        const userData = this.getUserDataInStorage();

        if (userData) {
          const userLoginData = this.userHelperService.getUserLoginData();
          const token = this.userHelperService.getIdToken();
          const userId = this.userHelperService.getUserAttributes()['custom:userId'];
          const { email, refreshToken } = userData;
          return this.authService.refreshToken({ email, refreshToken, userLoginData, token }).pipe(
            retryWhen((errors) =>
              errors.pipe(
                mergeMap((err: any, index) => {
                  const status = err?.status;
                  const message = `${err?.error?.message ?? err?.message ?? ''}`.toLowerCase();
                  const definitive =
                    status === HttpStatusCode.Unauthorized ||
                    status === HttpStatusCode.Forbidden ||
                    /revoked|expired|not authorized|invalid refresh/.test(message);
                  // Transient (network / 5xx / timeout): one bounded retry so a blip
                  // does not sign the user out. Definitive errors stop immediately.
                  if (definitive || index >= 1) {
                    return throwError(() => err);
                  }
                  return timer(2000);
                })
              )
            ),
            switchMap((result: any) => {
              if (result.userExternal) {
                // Impersonated refresh: re-apply the user overlay atomically BEFORE
                // releasing the queued/retried requests. Otherwise they observe the
                // brief admin window (saveDataToken writes the admin payload first),
                // leaving a split-brain state (header=impersonated, content=admin).
                // loginAs avoids this via a full reload; the refresh path has none.
                // applyImpersonatedRefresh resets the single-flight state itself, so
                // retrying THIS request is the only subscription-dependent step here.
                return from(this.applyImpersonatedRefresh(result)).pipe(
                  switchMap(() => next.handle(this.addTokenHeader(request, result.idToken.jwtToken)))
                );
              }
              // A rejected persist (quota, cookie write, ACL save) must not leave the
              // single-flight latched: isRefreshing stuck true parks every later 401 on
              // the BehaviorSubject, which replays the stale token forever (OMNI-7716).
              this.userHelperService.saveDataToken(result).finally(() => {
                this.isRefreshing = false;
              });
              this.refreshTokenSubject.next(result.idToken.jwtToken);
              return next.handle(this.addTokenHeader(request, result.idToken.jwtToken));
            }),
            catchError((err) => {
              this.isRefreshing = false;
              // stash BEFORE clear() — otherwise the user attributes are already wiped and the
              // report loses userId/customer (only browserId survives via KEEP_PREFIXES)
              this.authService.stashSignoutReason('refresh-failed');
              const dashboardLayout = localStorage.getItem('dashboard_layout');
              this.localStorageService.clear();
              if (dashboardLayout) {
                localStorage.setItem('dashboard_layout', dashboardLayout);
              }
              this.dialog.closeAll();
              const loggedOffReason = err?.error?.message;
              this.httpErrorsService.showDialogError({
                error: {
                  statusCode: 403,
                  message: loggedOffReason ?? 'EXPIRED_SESSION',
                },
              });
              this.signout();
              void this.spinner.hide();
              return throwError(err);
            })
          );
        } else {
          this.isRefreshing = false;
          this.authService.stashSignoutReason('no-user-data');
          this.signout();
          return throwError('User data not found!');
        }
      } catch (error) {
        this.isRefreshing = false;
        return throwError(error);
      }
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addTokenHeader(request, token)))
      );
    }
  }

  // Serializes the impersonated-refresh side effects so no request or UI reads the
  // transient admin state, then releases the retry. Hardened for the login path:
  //  - every failure is swallowed (a stale overlay is recoverable, a signout is not);
  //  - the overlay refetch is time-boxed so a hung request can never deadlock the
  //    retry (on timeout it degrades to the previous late-apply behaviour);
  //  - the single-flight state (isRefreshing / refreshTokenSubject) is reset inside
  //    release(), which runs from the promise itself — NOT from the observable's
  //    switchMap. If the request that triggered the refresh is unsubscribed mid-
  //    overlay (route change, takeUntil, switchMap cancel), an observable-side reset
  //    would never run and the interceptor would wedge (isRefreshing stuck true,
  //    every later 401 waiting on a subject that never emits).
  private applyImpersonatedRefresh(result: any): Promise<void> {
    return new Promise<void>((resolve) => {
      let settled = false;
      const release = () => {
        if (settled) {
          return;
        }
        settled = true;
        this.isRefreshing = false;
        this.refreshTokenSubject.next(result.idToken.jwtToken);
        resolve();
      };
      const timer = setTimeout(release, this.impersonatedOverlayTimeoutMs);
      void (async () => {
        try {
          await this.userHelperService.saveDataToken(result);
          await this.injector.get(AdminOtherUsersService).overrideUserCache(result.userExternal);
          // impersonation never swaps the idToken, so token-keyed server caches still
          // point at the admin; drop them so they refetch under the restored overlay.
          this.injector.get(CustomersService).invalidateCache();
        } catch {
          // overlay/persist blip must not sign out or block the retry
        } finally {
          clearTimeout(timer);
          release();
        }
      })();
    });
  }

  private signout() {
    this.userHelperService.clearAccessTokens();
    this.spinner.show();
    this.authService.signout();
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private getUserDataInStorage() {
    const refreshToken = this.userHelperService.getRefreshToken();
    const userData: any = this.userHelperService.getUserAttributes();
    if (refreshToken && userData) {
      return {
        refreshToken,
        email: userData['email'],
      };
    }
    return null;
  }
}
